import React from 'react';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import DatePicker from 'components/DatePicker';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AlarmIcon from '@mui/icons-material/Alarm';
import { Body1, Caption, CenteredMessage, Subtitle2 } from 'components/Typography';
import { DateTime, Duration } from 'luxon';
import { withStyles } from "tss-react/mui";
import { List as ImmutableList } from 'immutable';
import isNotNullOrEmpty from 'utilities/is/isNotNullOrEmpty';
import isDefined from 'utilities/is/isDefined';
import classNames from 'classnames';
import styles from './styles';
import { expiryIds } from '../../domain';
import { toShortDateString } from 'utilities/formatters/dateTimeFormatter';

function isImminent(sc) {
    const dateNext7Days = DateTime.local().startOf("day").plus(Duration.fromObject({ days: 7 }));
    const diff = sc.dueDate.diff(dateNext7Days).as("hours");
    return diff < 0;
}

function isComplete(sc) {
    return isDefined(sc.award);
}

const sort = (list) => ImmutableList(list).sortBy(sc => sc.name).toArray();

class ScheduledCompetencyList extends React.Component {

    state = {
        selectedCompetence: null,
        expiryDate: null
    };

    secondaryText(sc) {
        let imminent = isImminent(sc);
        let completed = isComplete(sc);

        const className = completed ? this.props.classes.complete : (imminent ? this.props.classes.imminent : '');
        const classes = classNames(className, this.props.classes.caption);
        return (
            <Caption className={classes}>
                {imminent && <AlarmIcon className={this.props.classes.imminentIcon} />}
                Due {toShortDateString(sc.dueDate)}{completed ? ' Complete' : ''}
            </Caption>);
    }

    emptyMessage() {
        return (
            <CenteredMessage className={this.props.classes.emptyMessage}>
                There are no scheduled competencies to award
            </CenteredMessage>
        );
    }

    onCompetenceClicked = (comp) => {
        this.setState({ selectedCompetence: comp });
        this.props.competencyClicked(comp);
    };

    showExpiryDate(scheduled) {
        if (!Boolean(scheduled)) {
            return false;
        }

        return (scheduled && scheduled.expiryID && (scheduled.expiryID.toUpperCase() === expiryIds.SPECIFY_EXPIRY_DATE));
    }

    setExpiryDate = (date) => {
        this.setState({ expiryDate: date });

        const today = DateTime.local().startOf('day');

        if (date && date.isValid && date.diff(today).as('minutes') >= 0) {
            this.props.handleExpiryDateSet(toShortDateString(date));
        } else {
            this.props.handleExpiryDateSet('');
        }
    };

    competencyList() {
        const { classes, onSecondaryAction, shouldShowSecondary } = this.props;
        const showSecondary = isDefined(onSecondaryAction) && isDefined(shouldShowSecondary);
        const showExpiryField = this.showExpiryDate(this.state.selectedCompetence);
        return (
            <List>
                {sort(this.props.scheduledCompetencies || []).map(sc =>
                    <ListItem
                        key={sc.scheduledCompetenceID}
                        button
                        onClick={() => this.onCompetenceClicked(sc)}
                        className={classNames(classes.listItemRoot, sc === this.state.selectedCompetence ? classes.selected : '')}>

                        <ListItemText>
                            <Grid container alignContent="flex-end" alignItems="baseline">
                                <Grid item xs={12} sm={12}>
                                    <Body1>{sc.name}</Body1>
                                </Grid>
                                <Grid container>
                                    <Grid item xs={12} sm={8}>
                                        <Caption className={classes.caption}> {sc.siteName}</Caption>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        {this.secondaryText(sc)}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </ListItemText>
                        {showSecondary && shouldShowSecondary(sc) &&
                            <ListItemSecondaryAction>
                                <IconButton onClick={(event) => onSecondaryAction(event, sc)} size="large" title="Select">
                                    <MoreVertIcon />
                                </IconButton>
                            </ListItemSecondaryAction>
                        }
                    </ListItem>
                )}
                {showExpiryField &&
                    <Grid container
                        alignItems="center"
                        alignContent="flex-start"
                        justifyContent="flex-start"
                        spacing={2}
                        className={classNames(classes.expiryPanel, showExpiryField ? '' : classes.disabled)}>
                        <Grid item xs={12} className={classes.expiryDateLabel}>
                            <Subtitle2>Expiry Date:</Subtitle2>
                        </Grid>
                        <Grid item xs={12}>
                            <DatePicker
                                name="expiry"
                                style={{ marginTop: -12 }}
                                disabled={!showExpiryField}
                                disablePast
                                value={this.state.expiryDate}
                                onChange={this.setExpiryDate}
                                openTo="year"
                                views={["year", "month", "day"]}
                            />
                        </Grid>
                    </Grid>
                }
            </List>
        );
    }

    render() {
        const isEmpty = !isNotNullOrEmpty(this.props.scheduledCompetencies);
        return isEmpty ? this.emptyMessage() : this.competencyList();
    }
}

export default withStyles(ScheduledCompetencyList, styles);
